import { useCallback, useMemo } from 'react'

import { DoneAllOutlined, ErrorOutlineOutlined, HourglassTop, Textsms } from '@mui/icons-material'
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material'

import { preview } from '@/components/form/preview'

import { SMSBody } from './SMSBody'
import { SentBy } from './SentBy'
import { Status } from './Status'

import { useTexts } from '../useTexts'

interface Props {
  item: uui.domain.client.rm.OrderStepSmsNotification
  showModal: (data: uui.domain.client.rm.OrderStepSmsNotification) => void
}

export function SMSNotificationItem(props: Props) {
  const { item, showModal } = props

  const texts = useTexts()

  const handleOnShowModal = useCallback(() => {
    showModal(item)
  }, [item, showModal])

  const status = useMemo(() => {
    if (item.status === 'FAILED') {
      const failedError = !!item.errorCode
        ? texts.errorCodeLabel(item.errorCode)
        : texts.failedStatus
      return (
        <Tooltip title={failedError} placement="bottom">
          <ErrorOutlineOutlined color="error" fontSize="small" />
        </Tooltip>
      )
    }
    if (item.status === 'PENDING')
      return (
        <Tooltip title={texts.pendingStatus} placement="bottom">
          <HourglassTop color="action" fontSize="small" />
        </Tooltip>
      )
    return (
      <Tooltip title={texts.completedStatus} placement="bottom">
        <DoneAllOutlined color="success" fontSize="small" />
      </Tooltip>
    )
  }, [item, texts])

  return (
    <preview.Section>
      <Paper elevation={0} variant="outlined" sx={{ padding: 2 }}>
        <Stack direction="column" spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="body1">
              <Box display="flex" alignItems="center">
                <Textsms sx={{ mr: 1 }} />
                {texts.sms}
              </Box>
            </Typography>

            <Status status={status} sentTs={item.sentTs} />
          </Stack>

          <Stack direction="column" spacing={1} alignItems="flex-start">
            <SMSBody body={item.bodyPlainText} />

            <Button
              data-testid="orderform_order-notification-tab-view-button"
              data-trackid="orderform_order-notification-tab-view-button"
              onClick={handleOnShowModal}
              size="small"
            >
              {texts.viewSettings}
            </Button>

            <SentBy recipient={item.recipient} sender={texts.senderText} />
          </Stack>
        </Stack>
      </Paper>
    </preview.Section>
  )
}
